import React, { useRef, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Collapse,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  AudioFile,
  ExpandMore,
  ExpandLess,
  Edit,
  Image as ImageIcon,
  AddPhotoAlternate,
} from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createAudio } from '../../http/audio';
import Spinner from '../../components/Spinner';
import CustomMultiSelect from '../../components/FormElements/MultiSelect';
import defaultAudioThumbnail from '../../assets/lightmodeicon.png';

const BulkUploadModal = ({
  openDialog,
  handleCloseDialog,
  classes,
  categoryTypes,
  fetchAudios,
  contests = [],
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [defaultCategories, setDefaultCategories] = useState([]);
  const [defaultContests, setDefaultContests] = useState([]);
  const [defaultImage, setDefaultImage] = useState(null);
  const [defaultImagePreview, setDefaultImagePreview] = useState(null);
  const [expandedFileIndex, setExpandedFileIndex] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB for images

  // Check if Contest category is selected
  const contestCategory = categoryTypes.find(
    (cat) => cat.name === 'Contest' || cat.categoryShortName === 'contest'
  );
  const isContestCategorySelected = (categories) => {
    if (!contestCategory) return false;
    return categories?.includes(contestCategory._id);
  };

  // Sort contests alphabetically by title
  const sortedContests = useMemo(() => {
    return [...contests].sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, [contests]);

  const processFiles = async (files) => {
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      // Check file type
      if (!file.type.startsWith('audio/')) {
        errors.push(`${file.name}: Not a valid audio file`);
        continue;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size exceeds 5MB limit`);
        continue;
      }

      // Check for duplicates and sanitize name
      const baseName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      const sanitizedName = sanitizeAudioName(baseName);
      
      if (selectedFiles.some((f) => f.name === sanitizedName)) {
        errors.push(`${file.name}: File already selected`);
        continue;
      }

      validFiles.push({
        file,
        name: sanitizedName, // Sanitized name without extension
        categories: null, // null means use default categories
        contests: null, // null means use default contests
        customImage: null, // null means use default image
        customImagePreview: null,
        status: 'pending', // pending, uploading, success, error
        progress: 0,
      });
    }

    if (errors.length > 0) {
      // Show only first 3 errors to avoid toast overflow
      errors.slice(0, 3).forEach((error) => toast.error(error));
      if (errors.length > 3) {
        toast.error(`And ${errors.length - 3} more files with errors...`);
      }
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  // Helper function to sanitize audio name (remove special characters)
  const sanitizeAudioName = (name) => {
    return name
      // Keep apostrophes (Don't Stop). Strip other characters that break names.
      .replace(/["`,;:!@#$%^&*()[\]{}|\\/<>?~`]/g, '')
      // Replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      // Trim whitespace
      .trim();
  };

  const handleDefaultCategoryChange = (selected) => {
    setDefaultCategories(selected);
    // Clear default contests if Contest category is removed
    if (!isContestCategorySelected(selected)) {
      setDefaultContests([]);
    }
  };

  const handleDefaultContestChange = (selected) => {
    setDefaultContests(selected);
  };

  const handleFileCategoryChange = (index, selected) => {
    setSelectedFiles((prev) => 
      prev.map((f, i) => {
        if (i === index) {
          const updated = { ...f, categories: selected };
          // Clear contests if Contest category is removed
          if (!isContestCategorySelected(selected)) {
            updated.contests = null;
          }
          return updated;
        }
        return f;
      })
    );
  };

  const handleFileContestChange = (index, selected) => {
    setSelectedFiles((prev) => 
      prev.map((f, i) => i === index ? { ...f, contests: selected } : f)
    );
  };

  const toggleFileExpand = (index) => {
    setExpandedFileIndex(expandedFileIndex === index ? null : index);
  };

  const getFileCategories = (fileData) => {
    // Use file-specific categories if set, otherwise use default
    return fileData.categories !== null ? fileData.categories : defaultCategories;
  };

  const getFileContests = (fileData) => {
    // Use file-specific contests if set, otherwise use default
    return fileData.contests !== null ? fileData.contests : defaultContests;
  };

  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) return 'No categories';
    return categoryIds
      .map(id => categoryTypes.find(c => c._id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const getContestNames = (contestIds) => {
    if (!contestIds || contestIds.length === 0) return 'None';
    return contestIds
      .map(id => contests.find(c => c._id === id)?.title)
      .filter(Boolean)
      .map(title => title.length > 15 ? title.slice(0, 15) + '...' : title)
      .join(', ');
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    await processFiles(files);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    await processFiles(files);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (expandedFileIndex === index) {
      setExpandedFileIndex(null);
    }
  };

  // Default image handlers
  const handleDefaultImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setDefaultImage(file);
    setDefaultImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveDefaultImage = () => {
    if (defaultImagePreview) {
      URL.revokeObjectURL(defaultImagePreview);
    }
    setDefaultImage(null);
    setDefaultImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Per-file custom image handlers
  const handleFileImageChange = (index, file) => {
    if (!file) {
      // Reset to default
      setSelectedFiles((prev) => {
        const updated = [...prev];
        if (updated[index].customImagePreview) {
          URL.revokeObjectURL(updated[index].customImagePreview);
        }
        updated[index] = { ...updated[index], customImage: null, customImagePreview: null };
        return updated;
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setSelectedFiles((prev) => {
      const updated = [...prev];
      if (updated[index].customImagePreview) {
        URL.revokeObjectURL(updated[index].customImagePreview);
      }
      updated[index] = {
        ...updated[index],
        customImage: file,
        customImagePreview: URL.createObjectURL(file),
      };
      return updated;
    });
  };

  // Get the image to use for a file (custom > default > fallback)
  const getFileImage = (fileData) => {
    if (fileData.customImage) return fileData.customImage;
    if (defaultImage) return defaultImage;
    return null; // Will use fallback in uploadSingleFile
  };

  const uploadSingleFile = async (fileData, index) => {
    const formData = new FormData();
    
    // IMPORTANT: Field order must match AddAudioModal for server compatibility
    // Order: 1. image, 2. name, 3. categories, 4. contests, 5. file
    
    // 1. Add image FIRST (matches AddAudioModal order)
    const imageToUse = getFileImage(fileData);
    
    if (imageToUse) {
      // User provided an image (custom or default)
      formData.append('image', imageToUse);
    } else {
      // Fallback to app icon - convert via canvas to ensure proper file creation
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = defaultAudioThumbnail;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width || 200;
        canvas.height = img.height || 200;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/png');
        });

        if (blob) {
          const imageFile = new File([blob], 'audio-thumbnail.png', { type: 'image/png' });
          formData.append('image', imageFile);
        }
      } catch (err) {
        console.error('Error creating fallback thumbnail:', err);
        // Continue without image - server may still accept the upload
      }
    }

    // 2. Add name
    formData.append('name', fileData.name);

    // 3. Add categories
    const fileCategories = getFileCategories(fileData);
    
    if (fileCategories && fileCategories.length > 0) {
      fileCategories.forEach((catId) => {
        formData.append('categoryIds[]', catId);
        formData.append(`categoryStatus[${catId}]`, 'ACTIVE');
      });
    } else {
      // Fallback to first category if nothing selected
      const fallbackCategory = categoryTypes[0]?._id;
      if (fallbackCategory) {
        formData.append('categoryIds[]', fallbackCategory);
        formData.append(`categoryStatus[${fallbackCategory}]`, 'ACTIVE');
      }
    }

    // 4. Add contests (only if Contest category is selected)
    if (isContestCategorySelected(fileCategories)) {
      const fileContests = getFileContests(fileData);
      if (fileContests && fileContests.length > 0) {
        fileContests.forEach((contestId) => {
          formData.append('contestIds[]', contestId);
        });
      }
    }

    // 5. Add audio file LAST (matches AddAudioModal order)
    formData.append('file', fileData.file);

    // Debug: Log what we're sending
    console.log('Uploading audio:', {
      name: fileData.name,
      fileSize: fileData.file.size,
      fileType: fileData.file.type,
      categories: fileCategories,
      hasImage: !!imageToUse,
    });

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [index]: { status: 'uploading', progress: 50 },
      }));

      await createAudio(formData, {
        mode: 'bulk-create',
        name: fileData.name,
        fileSize: fileData.file?.size,
        fileType: fileData.file?.type,
        categoryIds: fileCategories,
        contestIds: getFileContests(fileData),
        hasImage: !!imageToUse,
      });

      setUploadProgress((prev) => ({
        ...prev,
        [index]: { status: 'success', progress: 100 },
      }));

      return { success: true, name: fileData.name };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      console.error(`Error uploading ${fileData.name}:`, errorMessage, error);
      setUploadProgress((prev) => ({
        ...prev,
        [index]: { status: 'error', progress: 0 },
      }));
      return { success: false, name: fileData.name, error: errorMessage };
    }
  };

  const handleUploadAll = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one audio file');
      return;
    }

    if (categoryTypes.length === 0) {
      toast.error('No categories available. Please create a category first.');
      return;
    }

    setUploading(true);
    const results = [];
    let stoppedAtIndex = -1;

    // Upload files sequentially - stop on first error
    for (let i = 0; i < selectedFiles.length; i++) {
      const result = await uploadSingleFile(selectedFiles[i], i);
      results.push(result);
      
      // Stop immediately if an error occurs
      if (!result.success) {
        stoppedAtIndex = i;
        break;
      }
    }

    setUploading(false);

    const successCount = results.filter(r => r.success).length;
    const failedFile = results.find(r => !r.success);

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} audio file(s)`);
    }
    
    if (failedFile) {
      const remainingCount = selectedFiles.length - stoppedAtIndex - 1;
      toast.error(`Upload stopped: "${failedFile.name}" failed - ${failedFile.error}`);
      
      if (remainingCount > 0) {
        toast.info(`${remainingCount} file(s) remaining. Fix the error and try again.`);
      }
      
      // Remove successfully uploaded files from the list, keep failed + remaining
      setSelectedFiles((prev) => prev.slice(stoppedAtIndex));
      setUploadProgress({});
      
      console.error('Upload stopped at:', failedFile);
    }

    // Refresh the audio list
    await fetchAudios();

    // Close modal after a short delay if all successful
    if (!failedFile) {
      setTimeout(() => {
        handleClose();
      }, 1500);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      // Clean up image previews
      if (defaultImagePreview) {
        URL.revokeObjectURL(defaultImagePreview);
      }
      selectedFiles.forEach((file) => {
        if (file.customImagePreview) {
          URL.revokeObjectURL(file.customImagePreview);
        }
      });
      
      setSelectedFiles([]);
      setUploadProgress({});
      setDefaultCategories([]);
      setDefaultContests([]);
      setDefaultImage(null);
      setDefaultImagePreview(null);
      setExpandedFileIndex(null);
      handleCloseDialog();
    }
  };

  const getStatusIcon = (index) => {
    const progress = uploadProgress[index];
    if (!progress) return null;

    switch (progress.status) {
      case 'uploading':
        return <LinearProgress sx={{ width: 60, mr: 1 }} />;
      case 'success':
        return <CheckCircle sx={{ color: '#4CAF50', mr: 1 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#f44336', mr: 1 }} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      sx={{
        zIndex: 9,
        '& .MuiDialog-container .MuiPaper-elevation': {
          borderRadius: '8px',
          width: '550px',
          maxWidth: '90vw',
          maxHeight: '85vh',
        },
      }}
      open={openDialog}
      onClose={handleClose}
    >
      {uploading && <Spinner />}
      <DialogTitle
        sx={{
          padding: '20px 24px 10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography 
          sx={{ 
            fontWeight: 700, 
            fontSize: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Bulk Upload Audio
        </Typography>
        <IconButton 
          onClick={handleClose} 
          disabled={uploading} 
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
            },
          }}
        >
          <Close sx={{ color: '#667eea' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '16px 24px 24px' }}>
        {/* Default Category Selector */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>
            Default Categories for All Files
          </Typography>
          <CustomMultiSelect
            selectOptions={categoryTypes}
            onChange={handleDefaultCategoryChange}
            placeholder="Select default categories"
            value={defaultCategories}
          />
          {defaultCategories.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {defaultCategories.map(catId => {
                const cat = categoryTypes.find(c => c._id === catId);
                return cat ? (
                  <Chip
                    key={catId}
                    label={cat.name}
                    size="small"
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '11px' }}
                  />
                ) : null;
              })}
            </Box>
          )}
        </Box>

        {/* Default Contest Selector - only shows when Contest category is selected */}
        {isContestCategorySelected(defaultCategories) && contests.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>
              Default Contests for All Files
            </Typography>
            <CustomMultiSelect
              selectOptions={sortedContests}
              onChange={handleDefaultContestChange}
              placeholder="Select default contests"
              value={defaultContests}
            />
            {defaultContests.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {defaultContests.map(contestId => {
                  const contest = contests.find(c => c._id === contestId);
                  return contest ? (
                    <Chip
                      key={contestId}
                      label={contest.title?.slice(0, 20) + (contest.title?.length > 20 ? '...' : '')}
                      size="small"
                      sx={{ background: 'linear-gradient(135deg, #20c997 0%, #17a87d 100%)', color: 'white', fontSize: '11px' }}
                    />
                  ) : null;
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Default Image Upload */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>
            Default Thumbnail for All Files
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleDefaultImageSelect}
              ref={imageInputRef}
              style={{ display: 'none' }}
            />
            {defaultImagePreview ? (
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={defaultImagePreview}
                  alt="Default thumbnail"
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #667eea',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveDefaultImage}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: '#ff6b6b',
                    color: 'white',
                    width: 24,
                    height: 24,
                    '&:hover': { backgroundColor: '#e55555' },
                  }}
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                onClick={() => imageInputRef.current?.click()}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '8px',
                  border: '2px dashed #667eea',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f8f9ff',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#e8ebff',
                    borderColor: '#764ba2',
                  },
                }}
              >
                <AddPhotoAlternate sx={{ fontSize: 28, color: '#667eea' }} />
                <Typography sx={{ fontSize: '10px', color: '#667eea', mt: 0.5 }}>
                  Add Image
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '12px', color: '#666' }}>
                {defaultImagePreview 
                  ? 'This image will be used for all audio files unless overridden individually.'
                  : 'Click to upload a default thumbnail image. Optional - a placeholder will be used if not provided.'}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#999', mt: 0.5 }}>
                Max 2MB • Recommended: 200x200px
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* File selection area - supports drag & drop */}
        <Box
          sx={{
            border: isDragOver ? '2px dashed #4CAF50' : '2px dashed #667eea',
            borderRadius: '8px',
            padding: '30px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragOver ? '#e8fff6' : '#f8f9ff',
            transition: 'all 0.2s ease',
            transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
            '&:hover': {
              backgroundColor: '#f0f2ff',
              borderColor: '#764ba2',
            },
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept="audio/*"
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <CloudUpload sx={{ fontSize: 48, color: isDragOver ? '#20c997' : '#667eea', mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: isDragOver ? '#20c997' : '#667eea' }}>
            {isDragOver ? 'Drop audio files here' : 'Drag & drop or click to select audio files'}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
            Max 5MB each
          </Typography>
        </Box>

        {/* Info text */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: '6px' }}>
          <Typography sx={{ fontSize: '12px', color: '#1565c0' }}>
            <strong>Tip:</strong> Set default categories{isContestCategorySelected(defaultCategories) ? ' and contests' : ''} above. Click the edit icon on any file to customize individually.
          </Typography>
        </Box>

        {/* Selected files list */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>
              Selected Files ({selectedFiles.length})
            </Typography>
            <List
              sx={{
                maxHeight: '300px',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
              }}
            >
              {selectedFiles.map((fileData, index) => (
                <React.Fragment key={`${fileData.name}-${index}`}>
                  <ListItem
                    sx={{
                      borderBottom: expandedFileIndex !== index && index < selectedFiles.length - 1 
                        ? '1px solid #e0e0e0' 
                        : 'none',
                      py: 1,
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {/* Thumbnail preview */}
                      <Box sx={{ position: 'relative', mr: 2, flexShrink: 0 }}>
                        {fileData.customImagePreview || defaultImagePreview ? (
                          <Box
                            component="img"
                            src={fileData.customImagePreview || defaultImagePreview}
                            alt="Thumbnail"
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: fileData.customImagePreview ? '2px solid #764ba2' : '2px solid #667eea',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '6px',
                              backgroundColor: '#f0f2ff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <AudioFile sx={{ color: '#667eea', fontSize: 24 }} />
                          </Box>
                        )}
                        {fileData.customImagePreview && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -4,
                              right: -4,
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: '#764ba2',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 8, color: 'white' }} />
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {fileData.name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#666' }}>
                          {(fileData.file.size / 1024 / 1024).toFixed(2)} MB • {' '}
                          <span style={{ color: fileData.categories !== null ? '#4CAF50' : '#666' }}>
                            {fileData.categories !== null 
                              ? `Custom: ${getCategoryNames(fileData.categories)}` 
                              : `Default: ${getCategoryNames(defaultCategories)}`}
                          </span>
                          {isContestCategorySelected(getFileCategories(fileData)) && (
                            <>
                              {' • '}
                              <span style={{ color: fileData.contests !== null ? '#ff9800' : '#666' }}>
                                Contests: {fileData.contests !== null 
                                  ? getContestNames(fileData.contests) 
                                  : getContestNames(defaultContests)}
                              </span>
                            </>
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        {getStatusIcon(index)}
                        {!uploadProgress[index] && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => toggleFileExpand(index)}
                              disabled={uploading}
                              sx={{ mr: 0.5 }}
                            >
                              {expandedFileIndex === index ? (
                                <ExpandLess sx={{ color: '#142C5C' }} />
                              ) : (
                                <Edit sx={{ color: '#142C5C', fontSize: 18 }} />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFile(index)}
                              disabled={uploading}
                            >
                              <Delete sx={{ color: '#f44336', fontSize: 18 }} />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                  <Collapse in={expandedFileIndex === index} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, backgroundColor: '#f5f5f5', borderBottom: index < selectedFiles.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1, color: '#142C5C' }}>
                        Custom categories for this file:
                      </Typography>
                      <CustomMultiSelect
                        selectOptions={categoryTypes}
                        onChange={(selected) => handleFileCategoryChange(index, selected)}
                        placeholder="Select categories (leave empty for default)"
                        value={fileData.categories || []}
                      />
                      {fileData.categories !== null && (
                        <Button
                          size="small"
                          onClick={() => handleFileCategoryChange(index, null)}
                          sx={{ mt: 1, fontSize: '11px', color: '#666' }}
                        >
                          Reset to default categories
                        </Button>
                      )}

                      {/* Contest selection - only shows when Contest category is selected for this file */}
                      {isContestCategorySelected(getFileCategories(fileData)) && contests.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1, color: '#4CAF50' }}>
                            Custom contests for this file:
                          </Typography>
                          <CustomMultiSelect
                            selectOptions={sortedContests}
                            onChange={(selected) => handleFileContestChange(index, selected)}
                            placeholder="Select contests (leave empty for default)"
                            value={fileData.contests || []}
                          />
                          {fileData.contests !== null && (
                            <Button
                              size="small"
                              onClick={() => handleFileContestChange(index, null)}
                              sx={{ mt: 1, fontSize: '11px', color: '#666' }}
                            >
                              Reset to default contests
                            </Button>
                          )}
                        </Box>
                      )}

                      {/* Custom image for this file */}
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1, color: '#764ba2' }}>
                          Custom thumbnail for this file:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <input
                            type="file"
                            accept="image/*"
                            id={`file-image-${index}`}
                            onChange={(e) => handleFileImageChange(index, e.target.files?.[0])}
                            style={{ display: 'none' }}
                          />
                          {fileData.customImagePreview ? (
                            <Box sx={{ position: 'relative' }}>
                              <Box
                                component="img"
                                src={fileData.customImagePreview}
                                alt="Custom thumbnail"
                                sx={{
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '2px solid #764ba2',
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleFileImageChange(index, null)}
                                sx={{
                                  position: 'absolute',
                                  top: -6,
                                  right: -6,
                                  backgroundColor: '#ff6b6b',
                                  color: 'white',
                                  width: 20,
                                  height: 20,
                                  '&:hover': { backgroundColor: '#e55555' },
                                }}
                              >
                                <Close sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box
                              component="label"
                              htmlFor={`file-image-${index}`}
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '6px',
                                border: '2px dashed #764ba2',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                backgroundColor: '#faf5ff',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#f0e6ff',
                                },
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 20, color: '#764ba2' }} />
                              <Typography sx={{ fontSize: '8px', color: '#764ba2' }}>
                                Custom
                              </Typography>
                            </Box>
                          )}
                          <Typography sx={{ fontSize: '11px', color: '#888', flex: 1 }}>
                            {fileData.customImagePreview 
                              ? 'Custom image set for this file'
                              : defaultImagePreview 
                                ? 'Will use default image above'
                                : 'Will use placeholder image'}
                          </Typography>
                        </Box>
                        {fileData.customImage && (
                          <Button
                            size="small"
                            onClick={() => handleFileImageChange(index, null)}
                            sx={{ mt: 1, fontSize: '11px', color: '#666' }}
                          >
                            Reset to default image
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            sx={{
              border: '2px solid #94a3b8',
              color: '#64748B',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              padding: '10px 24px',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                color: '#1a1a2e',
                borderColor: '#64748B',
              },
            }}
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
              color: '#fff !important',
              borderRadius: '12px !important',
              fontSize: '14px !important',
              fontWeight: 600,
              padding: '10px 24px',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%) !important',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                opacity: 0.5,
              },
            }}
            onClick={handleUploadAll}
            disabled={uploading || selectedFiles.length === 0}
          >
            {uploading ? 'Uploading...' : `Upload All (${selectedFiles.length})`}
          </Button>
        </Box>
      </DialogContent>
      <ToastContainer />
    </Dialog>
  );
};

export default BulkUploadModal;



